import { Button, Callout, Card, ErrorBanner, Spinner, StatTile, Badge } from "@bruntworkwear-apps/ui";
import styles from "./ConnectionStatus.module.css";
import type { ConnectionStatusProps } from "./ConnectionStatus.types";

/** Markup for {@link ConnectionStatus}. Presentation only. */
export function ConnectionStatusView({
  config,
  isLoading,
  error,
  isSessionExpired,
  onRetry,
  onReauthenticate,
}: ConnectionStatusProps) {
  if (isSessionExpired) {
    return (
      <Card title="Session">
        <Callout tone="warning">
          You are not signed in. Continue to sign in with your BRUNT account.
        </Callout>
        <p />
        <Button onClick={onReauthenticate}>Sign in again</Button>
      </Card>
    );
  }

  if (error) {
    return (
      <ErrorBanner
        message={error.message}
        action={
          <Button variant="ghost" onClick={onRetry}>
            Retry
          </Button>
        }
      />
    );
  }

  if (isLoading || !config) {
    return (
      <Card title="Connection">
        <div className={styles.loading}>
          <Spinner size={16} label={null} />
          Contacting the API…
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card title="Signed in">
        <div className={styles.identity}>
          <span className={styles.email}>{config.user_email}</span>
          <Badge tone={config.is_development_identity ? "warning" : "success"}>
            {config.is_development_identity ? "local identity" : "verified by IAP"}
          </Badge>
          <Badge tone="neutral">{config.environment}</Badge>
        </div>
        {/*
         * The build the API is running, and how long that process has been up.
         * A server left running across a morning of edits is otherwise
         * invisible - it answers every request happily with stale code.
         */}
        <p className={styles.build}>
          API build <code>{config.revision}</code>, up since{" "}
          {new Date(config.started_at).toLocaleString()}
        </p>
        {config.is_development_identity && (
          <>
            <p />
            <Callout tone="warning">
              No IAP locally, so this identity is assumed rather than verified. The real
              sign-in path is only exercised in the development environment on GCP.
            </Callout>
          </>
        )}
      </Card>

      <div className={styles.grid}>
        <StatTile
          label="Landing rate"
          value={`$${config.defaults.landing_rate.toFixed(2)}`}
          hint="per unit, editable per run"
          tone="brand"
        />
        <StatTile
          label="Lookback window"
          value={`${config.defaults.ops_lookback_days} days`}
          hint="Ops tracker rows considered"
        />
        <StatTile
          label="Variance threshold"
          value={`$${config.defaults.customs_variance_threshold.toFixed(0)}`}
          hint="below this, not adjusted"
          tone="muted"
        />
        <StatTile
          label="Upload limit"
          value={`${config.defaults.max_upload_mb} MB`}
          hint="per workbook"
        />
      </div>
    </>
  );
}
